import * as React from 'react'
import { Form, Formik } from 'formik'

import { CloseWinOpportunityInput } from '@user/store/opportunity'

import { Footer } from './Footer'
import { CustomField } from './CustomField'
import { SalesNoteField } from './SalesNoteField'
import Alert from '@mui/material/Alert'
import { Panel, CircularProgress } from '../atoms'
import { useGetAppDataOnMount, useHandlers } from './hooks'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import { ENTITY_TYPES } from '@sales-center/common'
import { SOLD_SERVICES_CONTENT_MODE } from '../typings'
import { IReferral } from '@sales-center/common'

export type AdditionalInfoEditProps = {
  readonly: boolean
  entityId: number
  entityType: ENTITY_TYPES
  closedWonStageId: number
  footerInstance: Element | null
  salesNote?: string
  referral?: IReferral
  onCloseWin: (input: CloseWinOpportunityInput) => Promise<void>
} & (
  | EditOppProps
  | NewOppProps
  | ConvertLeadProps
  | DuplicateOppProps
  | CloneOppProps
)

type EditOppProps = {
  type: SOLD_SERVICES_CONTENT_MODE.EditOpp
  branchId?: never
}

type NewOppProps = {
  branchId: string
  type: SOLD_SERVICES_CONTENT_MODE.NewOpp
}

type ConvertLeadProps = {
  branchId: string
  type: SOLD_SERVICES_CONTENT_MODE.ConvertLead
}

type DuplicateOppProps = {
  branchId: string
  type: SOLD_SERVICES_CONTENT_MODE.DuplicateOpp
}

type CloneOppProps = {
  branchId: string
  type: SOLD_SERVICES_CONTENT_MODE.CloneOpp
}

export function AdditionalInfoEdit(props: AdditionalInfoEditProps) {
  const {
    readonly,
    entityId,
    closedWonStageId,
    onCloseWin,
    salesNote,
    referral,
  } = props

  const { status, appData, initialValues } = useGetAppDataOnMount(props)

  const { onSubmit, onCancel } = useHandlers({
    appData,
    entityId,
    onCloseWin,
    closedWonStageId,
  })

  return (
    <Panel title="Additional Information" defaultExpanded>
      {status === 'pending' && <CircularProgress />}

      {status === 'fail' && (
        <Alert severity="error">Failed to load Custom Fields.</Alert>
      )}

      {status === 'success' && (
        <Formik
          onSubmit={onSubmit}
          initialValues={
            salesNote || referral
              ? {
                  ...initialValues,
                  salesNote:
                    salesNote && salesNote.trim().length > 0
                      ? salesNote
                      : referral?.referralLocationCode
                      ? `Referral from: ${referral?.referralLocationCode} - Referral code: ${referral?.referralDiscountCode}`
                      : '',
                }
              : { ...initialValues }
          }
        >
          {({ submitForm, isSubmitting }) => (
            <Form style={{ width: '100%' }}>
              <Typography style={{ fontFamily: 'Source Sans Pro SemiBold' }}>
                Technicians / Sales People
              </Typography>

              {appData.map((a) => (
                <CustomField
                  key={a.id}
                  appData={a}
                  disabled={readonly || isSubmitting}
                />
              ))}

              <SalesNoteField {...props} disabled={readonly || isSubmitting} />

              {!readonly && (
                <Box mt={3}>
                  <Alert severity="info">
                    Closing this opportunity will submit the above information
                    to the closed/won queue.
                  </Alert>
                </Box>
              )}

              <Footer
                {...props}
                onCancel={onCancel}
                onSubmit={submitForm}
                isSubmitting={isSubmitting}
              />
            </Form>
          )}
        </Formik>
      )}
    </Panel>
  )
}
